import { useFormContext } from 'core/form/FormContext';
import { RollupFunction } from '@kentik/ui-shared/query/query.types';
import React, { useEffect, useMemo } from 'react';
import { getMeasurementItem } from '@kentik/ui-shared/query/measurements';
import { getRollupKey } from '@kentik/ui-shared/query/query';
import Section from 'app/views/boards/SidebarSection';
import { TbChartBar } from 'react-icons/tb';
import { Flex } from 'core/components';
import { Field, Select } from 'core/form';
import { observer } from 'mobx-react';
import { useDictionaryMeasurement } from 'app/stores/dictionary/useDictionaryMeasurements';
import { FieldState } from './filters/filter.types';

const AGGREGATE_VIZ_TYPES = ['bar', 'pie', 'sunburst'];
const EMPTY_ARRAY: unknown[] = [];

const YAxisMetricSection = () => {
  const form = useFormContext();
  const measurementName = form?.getValue('config.data.measurement') as string;
  const metrics = (form?.getValue('config.data.metrics') || EMPTY_ARRAY) as { name: string }[];
  const rollups = (form?.getValue('config.data.rollups') || EMPTY_ARRAY) as { metric: string; fn: RollupFunction }[];
  const chartType = form?.getValue('config.chart.type') as string;
  const selectedMetric = form?.getValue('config.chart.yAxis.metric') as string;
  const isAggregate = AGGREGATE_VIZ_TYPES.includes(chartType);
  const { measurement } = useDictionaryMeasurement(measurementName);

  const options = useMemo(() => {
    const opts: { label: string; value: string }[] = [];

    metrics.forEach((m) => {
      const item = measurement && getMeasurementItem(measurement, 'metric', m.name);
      const metricLabel = item?.Label || m.name;

      if (isAggregate) {
        // Aggregate charts: show rollup-level options (avg, max, p95 each produce different values)
        const metricRollups = rollups.filter((r) => r.metric === m.name);
        if (metricRollups.length === 0) {
          opts.push({ label: metricLabel, value: m.name });
        } else {
          metricRollups.forEach((r) => {
            const key = getRollupKey(r);
            opts.push({ label: `${r.fn} ${metricLabel}`, value: key });
          });
        }
      } else {
        // Time series charts: show metric-level options (all rollups share the same line data)
        opts.push({ label: metricLabel, value: m.name });
      }
    });

    return opts;
  }, [metrics, measurement, isAggregate, rollups]);

  useEffect(() => {
    if (!form) {
      return;
    }

    const yAxisMetricField = form.getField('config.chart.yAxis.metric') as FieldState<string>;

    yAxisMetricField.setOptions(options);

    // selectedMetric hasn't been set
    if (!selectedMetric && options.length > 0) {
      yAxisMetricField.setValue(options[0].value);
    }

    // selectedMetric is no longer valid
    if (selectedMetric && !options.find((o) => o.value === selectedMetric)) {
      yAxisMetricField.setValue(options.length > 0 ? options[0].value : '');
    }

    yAxisMetricField.validateWithDebounce();
  }, [form, selectedMetric, options]);

  if (options.length <= 1) {
    return null;
  }

  return (
    <Section title="Y-Axis" icon={TbChartBar}>
      <Flex flexDirection="column" px={2} gap={1} pt={1}>
        <Field name="config.chart.yAxis.metric" m={0} options={options}>
          <Select fill />
        </Field>
      </Flex>
    </Section>
  );
};

export default observer(YAxisMetricSection);
